/**
 * Source of truth for the active "view as customer" (impersonation) target.
 *
 * Backed by localStorage, mirrored into a module-level cache. The cache lets the
 * plain API-client module (`lib/api.ts`) read the current scope synchronously on
 * every request, without reaching into React or touching localStorage; the
 * localStorage backing makes that scope durable in two ways the previous
 * in-memory-only value was not:
 *
 *  - It survives a full page refresh, so reloading no longer silently drops you
 *    back into your own account.
 *  - It is shared across tabs of the same origin, so opening a trace in a new tab
 *    (the "View trace" links use target="_blank") keeps the same "Viewing as…"
 *    scope instead of snapping back to the staff user's own (empty) account.
 *
 * Safety: a persisted target must never leak into a NON-staff session (the
 * original reason this was kept in memory — a stale value once showed the
 * "Viewing as…" banner to the next user in the same tab). That invariant is
 * enforced in `ImpersonationProvider`, which clears the value as soon as auth
 * resolves to a non-staff user, and on sign-out (`SidebarUserMenu`), which calls
 * `stopImpersonation()` before tearing down the session. Both paths funnel
 * through `setImpersonationDomain(null)`, which clears localStorage too. The
 * backend independently honors the resulting `X-Impersonate-Domain` header only
 * for `@composo.ai` staff (see `get_api_key_or_user`); this is a UX scope, never
 * a security gate.
 *
 * Scope changes also remount the route tree (see `AppRouter`) so every page
 * refetches against the new domain.
 */
const STORAGE_KEY = "composo.impersonationDomain";

function readPersistedDomain(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage can be unavailable (private mode, disabled, SSR). Degrade to
    // a memory-only value for the life of the page rather than crashing.
    return null;
  }
}

// Seeded from localStorage at module load so the first synchronous read in a
// freshly loaded tab (new tab or refresh) already reflects the persisted scope.
let currentImpersonationDomain: string | null = readPersistedDomain();

export function getImpersonationDomain(): string | null {
  return currentImpersonationDomain;
}

export function setImpersonationDomain(domain: string | null): void {
  currentImpersonationDomain = domain;
  try {
    if (domain === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, domain);
    }
  } catch {
    // Persistence is best-effort; the in-memory value still scopes this page.
  }
}

/**
 * Subscribe to impersonation changes made in OTHER tabs. The `storage` event
 * fires only in tabs that did NOT make the change, so this is what propagates a
 * start/exit/sign-out in one tab to the rest: it keeps the in-memory cache in
 * sync and invokes `onChange` so React can re-render (banner + route remount).
 * Returns an unsubscribe function.
 */
export function subscribeToImpersonationChanges(
  onChange: (domain: string | null) => void
): () => void {
  const handler = (event: StorageEvent) => {
    // key === null means localStorage was cleared wholesale; otherwise ignore
    // keys that aren't ours. In both the removeItem and clear cases newValue is
    // null, which is exactly the "stopped impersonating" state we want.
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    const next = event.key === null ? null : event.newValue;
    currentImpersonationDomain = next;
    onChange(next);
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
